import { api } from "./api";

// Job polling for the dashboard.
//
// Long videos legitimately take longer than the old 30-minute client cap, and
// the old loop also aborted the whole generation on a single transient poll
// error. Both made healthy long jobs look failed. This poller:
//   * keeps tracking until the SERVER decides (the backend's own stale sweeper
//     stops a job at 180 min, so 195 min of client patience always ends with a
//     real server verdict, never a guess),
//   * tolerates transient network/proxy blips instead of failing the job,
//   * slows its own cadence down on long jobs to cut request volume,
//   * and never claims failure on its own — if it does stop early it says the
//     job is still running in the background.

export const PHASE_LABEL = {
  queued: "Queuing job...",
  "checking-quota": "Checking quota...",
  "fetching-youtube": "Fetching YouTube audio...",
  "fetching-instagram": "Fetching Reel audio...",
  "fetching-tiktok": "Fetching TikTok audio...",
  "downloading-from-r2": "Preparing your media...",
  "parsing-subtitles": "Reading your subtitle file...",
  transcribing: "Transcribing with Whisper...",
  translating: "Translating with the LinguaSub engine...",
  done: "Done",
  failed: "Failed",
};

export const TERMINAL_STATUSES = ["done", "done_with_warnings", "failed"];

// 195 min > the backend's 180-min stale-job sweeper, so the loop always
// outlives the server's own verdict rather than inventing a timeout.
export const POLL_GIVE_UP_MS = 195 * 60 * 1000;
const MAX_CONSECUTIVE_ERRORS = 10;

export const phaseLabel = (doc) => {
  const phase = doc?.phase;
  const p = doc?.progress;
  if (phase === "transcribing" && p?.chunks_total) {
    const pct = Math.floor((100 * (p.chunks_done || 0)) / p.chunks_total);
    return `Transcribing with Whisper... ${p.chunks_done}/${p.chunks_total} chunks (${pct}%)`;
  }
  if (phase === "transcribing" && p?.resumed_chunks) {
    return `Resuming transcription (${p.resumed_chunks} chunks recovered)...`;
  }
  if (phase === "translating" && p?.stage === "translating"
      && Number.isInteger(p.segments_done) && p.segments_done >= 0
      && Number.isInteger(p.segments_total) && p.segments_total > 0
      && p.segments_done <= p.segments_total) {
    const pct = Math.floor((100 * p.segments_done) / p.segments_total);
    return `${PHASE_LABEL.translating} ${p.segments_done}/${p.segments_total} segments (${pct}%)`;
  }
  return PHASE_LABEL[phase] || phase || "Working...";
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Snappy for short clips, calm for long ones.
const intervalFor = (elapsedMs) => {
  if (elapsedMs < 2 * 60 * 1000) return 2000;
  if (elapsedMs < 10 * 60 * 1000) return 5000;
  return 10000;
};

export async function pollJobUntilDone(jobId, onUpdate, { giveUpMs = POLL_GIVE_UP_MS } = {}) {
  const started = Date.now();
  let errors = 0;
  while (Date.now() - started < giveUpMs) {
    const elapsed = Date.now() - started;
    await sleep(intervalFor(elapsed));
    let doc;
    try {
      const res = await api.get(`/subtitles/${jobId}`);
      doc = res.data;
      errors = 0;
    } catch (e) {
      errors += 1;
      if (errors >= MAX_CONSECUTIVE_ERRORS) {
        return { _stillRunning: true, job_id: jobId, reason: "connection" };
      }
      continue;
    }
    onUpdate?.(phaseLabel(doc), doc);
    if (TERMINAL_STATUSES.includes(doc.status)) return doc;
  }
  return { _stillRunning: true, job_id: jobId, reason: "patience" };
}
